import React, { useState } from 'react'
import { useQuery } from 'react-query'
import {
  Shield,
  Download,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Settings,
  Eye,
  Play,
  Pause,
  X
} from 'lucide-react'
import { unifiedAdminApiClient as adminApi } from '../services/unified-admin-api-client'
import { useMLSExportUpdates } from '../services/websocket'
import { MLSProvider, MLSExportBatch, MLSFilters } from '../types'

export default function MLSPage() {
  const [activeTab, setActiveTab] = useState('exports')
  const [filters, setFilters] = useState<MLSFilters>({})
  const [page, setPage] = useState(1)

  // Real-time updates
  useMLSExportUpdates((data) => {
    // Handle real-time MLS export updates
    console.log('MLS Export Update:', data)
  })

  const { data: providersData, isLoading: providersLoading } = useQuery(
    'mls-providers',
    () => adminApi.getMLSProviders()
  )

  const { data: exportsData, isLoading: exportsLoading } = useQuery(
    ['mls-exports', page, filters],
    () => adminApi.getMLSExportBatches(page, 20, filters),
    {
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  )

  const providers = providersData?.data || []
  const exports = exportsData?.data || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            MLS Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Monitor MLS export batches and manage provider configurations
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('exports')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'exports'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Export Batches
          </button>
          <button
            onClick={() => setActiveTab('providers')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'providers'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Providers
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'analytics'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Analytics
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'exports' && (
        <ExportBatchesTab 
          exports={exports} 
          isLoading={exportsLoading}
          filters={filters}
          onFiltersChange={setFilters}
          page={page}
          onPageChange={setPage}
        />
      )}
      {activeTab === 'providers' && (
        <ProvidersTab providers={providers} isLoading={providersLoading} />
      )}
      {activeTab === 'analytics' && <AnalyticsTab />}
    </div>
  )
}

interface ExportBatchesTabProps {
  exports: MLSExportBatch[]
  isLoading: boolean
  filters: MLSFilters
  onFiltersChange: (filters: MLSFilters) => void
  page: number
  onPageChange: (page: number) => void
}

function ExportBatchesTab({ exports, isLoading, filters, onFiltersChange }: ExportBatchesTabProps) {
  if (isLoading) {
    return <div className="animate-pulse">Loading...</div>
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <select
              className="input"
              value={filters.status || ''}
              onChange={(e) => onFiltersChange({ ...filters, status: e.target.value || undefined })}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
            
            <input
              type="date"
              className="input"
              placeholder="Created After"
              value={filters.created_after || ''}
              onChange={(e) => onFiltersChange({ ...filters, created_after: e.target.value || undefined })}
            />
            
            <input
              type="text"
              className="input"
              placeholder="User ID"
              value={filters.user_id || ''}
              onChange={(e) => onFiltersChange({ ...filters, user_id: e.target.value || undefined })}
            />
            
            <button className="btn-secondary">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Export Batches Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Batch ID</th>
                  <th>User</th>
                  <th>Provider</th>
                  <th>Images</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {exports.map((batch) => (
                  <ExportBatchRow key={batch.id} batch={batch} />
                ))}
              </tbody>
            </table>
          </div>

          {exports.length === 0 && (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No export batches</h3>
              <p className="mt-1 text-sm text-gray-500">
                No MLS export batches found matching your criteria.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function ExportBatchRow({ batch }: { batch: MLSExportBatch }) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'failed':
        return <AlertTriangle className="h-4 w-4 text-red-500" />
      case 'processing':
        return <Clock className="h-4 w-4 text-yellow-500" />
      default:
        return <Clock className="h-4 w-4 text-gray-500" />
    }
  }

  const getStatusBadge = (status: string) => {
    const badgeClass = {
      pending: 'badge-gray',
      processing: 'badge-warning',
      completed: 'badge-success',
      failed: 'badge-danger',
      completed_with_errors: 'badge-warning'
    }[status] || 'badge-gray'

    return <span className={`badge ${badgeClass}`}>{status}</span>
  }

  const progress = batch.progress_info
  const progressPercentage = progress 
    ? Math.round((progress.processed / progress.total) * 100)
    : 0

  return (
    <tr>
      <td className="font-mono text-sm">{batch.id.slice(0, 8)}...</td>
      <td>{batch.user_id.slice(0, 8)}...</td>
      <td>{batch.provider_id.slice(0, 8)}...</td>
      <td>{batch.image_ids.length}</td>
      <td>
        <div className="flex items-center space-x-2">
          {getStatusIcon(batch.status)}
          {getStatusBadge(batch.status)}
        </div>
      </td>
      <td>
        {progress && (
          <div className="flex items-center space-x-2">
            <div className="w-16 bg-gray-200 rounded-full h-2">
              <div 
                className="bg-primary-600 h-2 rounded-full" 
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
            <span className="text-xs text-gray-500">
              {progress.processed}/{progress.total}
            </span>
          </div>
        )}
      </td>
      <td>{new Date(batch.created_at).toLocaleDateString()}</td>
      <td>
        <div className="flex items-center space-x-2">
          <button className="text-primary-600 hover:text-primary-900">
            <Eye className="h-4 w-4" />
          </button>
          {batch.download_url && (
            <a 
              href={batch.download_url}
              className="text-green-600 hover:text-green-900"
              download
            >
              <Download className="h-4 w-4" />
            </a>
          )}
        </div>
      </td>
    </tr>
  )
}

function ProvidersTab({ providers, isLoading }: { providers: MLSProvider[], isLoading: boolean }) {
  if (isLoading) {
    return <div className="animate-pulse">Loading...</div>
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {providers.map((provider) => (
          <ProviderCard key={provider.id} provider={provider} />
        ))}
      </div>
    </div>
  )
}

function ProviderCard({ provider }: { provider: MLSProvider }) {
  return (
    <div className="card">
      <div className="card-body">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">{provider.name}</h3>
          <span className={`badge ${provider.is_active ? 'badge-success' : 'badge-gray'}`}>
            {provider.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
        
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Type</span>
            <span className="font-medium capitalize">{provider.provider_type}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Code</span>
            <span className="font-medium">{provider.short_code}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Format</span>
            <span className="font-medium uppercase">{provider.required_format}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Max Size</span>
            <span className="font-medium">{provider.max_file_size_kb}KB</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Dimensions</span>
            <span className="font-medium">
              {provider.recommended_width}x{provider.recommended_height}
            </span>
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-gray-200">
          <button className="btn-secondary w-full">
            <Settings className="h-4 w-4 mr-2" />
            Configure
          </button>
        </div>
      </div>
    </div>
  )
}

function AnalyticsTab() {
  // Mock analytics data
  const stats = {
    totalExports: 1234,
    successRate: 94.5,
    avgProcessingTime: 45,
    topProviders: [
      { name: 'FlexMLS', exports: 456, success_rate: 96.2 },
      { name: 'CRMLS', exports: 321, success_rate: 93.8 },
      { name: 'Bright MLS', exports: 287, success_rate: 95.1 },
    ]
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="stat-card">
          <div className="stat-value">{stats.totalExports.toLocaleString()}</div>
          <div className="stat-label">Total Exports</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.successRate}%</div>
          <div className="stat-label">Success Rate</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.avgProcessingTime}s</div>
          <div className="stat-label">Avg Processing Time</div>
        </div>
      </div>

      {/* Top Providers */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium">Top Providers</h3>
        </div>
        <div className="card-body">
          <div className="space-y-4">
            {stats.topProviders.map((provider, index) => (
              <div key={provider.name} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                  <span className="font-medium text-gray-900">{provider.name}</span>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-500">{provider.exports} exports</span>
                  <span className="text-sm font-medium text-green-600">
                    {provider.success_rate}% success
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}